import {
  UnsupportedChecklistKindErrorPage,
  hrocChecklistsFeatureTranslations
} from '@features/hroc-checklists'
import {jobsToBeDoneFeatureTranslations} from '@features/jobs-to-be-done'
import {majorOutageLeadDashboardFeatureTranslations} from '@features/major-outage-lead-dashboard'
import {optimizationSpecialistDashboardFeatureTranslations} from '@features/optimization-specialist-dashboard'
import {performanceReviewFeatureTranslations} from '@features/performance-review'
import {processEngineerDashboardFeatureTranslations} from '@features/process-engineer-dashboard'
import {rcfaComplianceFeatureTranslations} from '@features/rcfa-compliance'
import {reliabilityEngineerDashboardFeatureTranslations} from '@features/reliability-engineer-dashboard'
import {slaTrackingFeatureTranslations} from '@features/sla-tracking'
import {systemsEngineerDashboardFeatureTranslations} from '@features/systems-engineer-dashboard'
import {Permission, PermissionType} from '@hconnect/apiclient'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {Route, Outlet} from 'react-router'

import {
  ConfigProviderWrapper,
  PermissionsContextProvider,
  TranslationPrefixProvider
} from '../shared/hooks'
import {DefaultShell} from '../shared/layouts'
import {Context} from '../shared/types'

import {
  AreaPlantSelectByPermission,
  AreaSelectByPermission,
  AreaReliabilityEngineerDashboard,
  ChecklistsTemplatesPage,
  OptimizationSpecialistDashboard,
  PlantChecklistPage,
  PlantSelectByPermission,
  ProcessEngineerDashboard,
  RcfaCompliancePage,
  ReliabilityEngineerDashboard,
  HierarchyLocationAccessRedirect,
  AreaProcessEngineerDashboard,
  AreaOptimizationSpecialistDashboard,
  HierarchyPlantAccessRedirect,
  PerformanceReviewPage,
  MajorOutageLeadDashboard,
  SystemsEngineerDashboard,
  ServiceLevelAgreementTrackingDashboard,
  JobsToBeDoneDashboard
} from './pages'
import {PlantSelectByPermissions} from './pages/location-select'
import {
  PlantPermissionGuardedRoute,
  PlantShiftsGuardedRoute,
  PlantPermissionsGuardedRoute
} from './routing'
import {
  CHECKLISTS,
  OPTIMIZATION_SPECIALIST_DASHBOARD,
  PLANT_CHECKLISTS_TEMPLATES,
  PLANT_CHECKLIST,
  PLANT_CHECKLIST_UNSUPPORTED_KIND,
  RELIABILITY_ENGINEER_DASHBOARD,
  PROCESS_ENGINEER_DASHBOARD,
  PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD,
  RCFA_COMPLIANCE,
  HierarchyRouteSegment,
  PERFORMANCE_REVIEW,
  MAJOR_OUTAGE_LEAD_DASHBOARD,
  PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD,
  PLANT_PERFORMANCE_REVIEW,
  SYSTEMS_ENGINEER_DASHBOARD,
  PLANT_SYSTEMS_ENGINEER_DASHBOARD,
  SERVICE_LEVEL_AGREEMENT_DASHBOARD,
  PLANT_SERVICE_LEVEL_AGREEMENT_DASHBOARD,
  JOBS_TO_BE_DONE_DASHBOARD,
  PLANT_JOBS_TO_BE_DONE_DASHBOARD
} from './routing/routes'
import {
  hasChecklistsPermission,
  hasViewReliabilityEngineerDashboardPermission,
  hasViewProcessEngineerDashboardPermission,
  hasViewOptimizationSpecialistDashboardPermission,
  hasRcfaCompliancePagePermission,
  hasPerformanceReviewPermission,
  hasMajorOutageLeadDashboardPermission,
  hasSystemsEngineerDashboardPermission,
  hasServiceLevelAgreementTrackingPermission,
  hasJobsToBeDonePermission
} from './utils'

const Wrapper: React.FC<{
  context: Context
  permissions: Permission[]
  children: React.ReactNode
}> = ({children, context, permissions}) => (
  <TranslationPrefixProvider>
    <PermissionsContextProvider initialState={permissions}>
      <DefaultShell context={context}>{children}</DefaultShell>
    </PermissionsContextProvider>
  </TranslationPrefixProvider>
)

export const getHrocRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  return (
    <>
      {renderChecklistsRoutes({context, permissions})}
      {renderProcessEngineerDashboardRoutes({context, permissions})}
      {renderReliabilityEngineerDashboardRoutes({context, permissions})}
      {renderOptimizationSpecialistDashboardRoutes({context, permissions})}
      {renderMajorOutageLeadDashboardRoutes({context, permissions})}
      {renderSystemsEngineerDashboardRoutes({context, permissions})}
      {renderServiceLevelAgreementTrackingDashboardRoutes({context, permissions})}
      {renderRcfaComplianceDashboardRoutes({context, permissions})}
      {renderPerformanceReviewRoutes({context, permissions})}
      {renderJobsToBeDoneDashboardRoutes({context, permissions})}
    </>
  )
}

const renderRcfaComplianceDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasRcfaCompliancePagePermission(permissions))
    return (
      <Route
        key="rcfa-complience-dashboard"
        path={RCFA_COMPLIANCE}
        element={
          <Wrapper context={context} permissions={permissions}>
            <FeatureTranslationsLoader translations={rcfaComplianceFeatureTranslations}>
              <RcfaCompliancePage />
            </FeatureTranslationsLoader>
          </Wrapper>
        }
      />
    )

  return null
}

const renderProcessEngineerDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (!hasViewProcessEngineerDashboardPermission(permissions)) return null
  return getLocationBasedNavigationRoutes({
    key: 'process-engineer-dashboard-routes',
    context,
    permissions,
    basePath: PROCESS_ENGINEER_DASHBOARD,
    permission: 'VIEW_PROCESS_ENGINEER_DASHBOARD',
    areaRouteElement: (
      <FeatureTranslationsLoader translations={processEngineerDashboardFeatureTranslations}>
        <AreaProcessEngineerDashboard />
      </FeatureTranslationsLoader>
    ),
    plantRouteElement: (
      <FeatureTranslationsLoader translations={processEngineerDashboardFeatureTranslations}>
        <ConfigProviderWrapper>
          <ProcessEngineerDashboard />
        </ConfigProviderWrapper>
      </FeatureTranslationsLoader>
    )
  })
}

const renderMajorOutageLeadDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasMajorOutageLeadDashboardPermission(permissions))
    return (
      <>
        <Route
          key="major-outage-lead-dashboard"
          path={MAJOR_OUTAGE_LEAD_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermission
                path={PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD}
                permission="VIEW_MAJOR_OUTAGE_LEAD_DASHBOARD"
              />
            </Wrapper>
          }
        />
        <Route
          key="plant-major-outage-lead-dashboard"
          path={PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionGuardedRoute
                permission="VIEW_MAJOR_OUTAGE_LEAD_DASHBOARD"
                plantSelectPath={MAJOR_OUTAGE_LEAD_DASHBOARD}
              >
                <FeatureTranslationsLoader
                  translations={majorOutageLeadDashboardFeatureTranslations}
                >
                  <ConfigProviderWrapper>
                    <MajorOutageLeadDashboard permission="VIEW_MAJOR_OUTAGE_LEAD_DASHBOARD" />
                  </ConfigProviderWrapper>
                </FeatureTranslationsLoader>
              </PlantPermissionGuardedRoute>
            </Wrapper>
          }
        />
      </>
    )

  return null
}

const renderPerformanceReviewRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasPerformanceReviewPermission(permissions))
    return (
      <>
        <Route
          key="performance-plant-select"
          path={PERFORMANCE_REVIEW}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermission
                path={PLANT_PERFORMANCE_REVIEW}
                permission="VIEW_PERFORMANCE_REVIEW"
              />
            </Wrapper>
          }
        />
        <Route
          key="plant-performance-review"
          path={PLANT_PERFORMANCE_REVIEW}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionGuardedRoute
                permission="VIEW_PERFORMANCE_REVIEW"
                plantSelectPath={PERFORMANCE_REVIEW}
              >
                <FeatureTranslationsLoader translations={performanceReviewFeatureTranslations}>
                  <ConfigProviderWrapper>
                    <PerformanceReviewPage permission="VIEW_PERFORMANCE_REVIEW" />
                  </ConfigProviderWrapper>
                </FeatureTranslationsLoader>
              </PlantPermissionGuardedRoute>
            </Wrapper>
          }
        />
      </>
    )

  return null
}

const renderChecklistsRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasChecklistsPermission(permissions))
    return (
      <>
        <Route
          key="checklists-plant-select"
          path={CHECKLISTS}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermission
                path={PLANT_CHECKLISTS_TEMPLATES}
                permission="VIEW_CHECKLISTS"
              />
            </Wrapper>
          }
        />
        <Route
          path={PLANT_CHECKLISTS_TEMPLATES}
          key="checklists-templates"
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionGuardedRoute
                permission="VIEW_CHECKLISTS"
                plantSelectPath={CHECKLISTS}
              >
                <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                  <ChecklistsTemplatesPage />
                </FeatureTranslationsLoader>
              </PlantPermissionGuardedRoute>
            </Wrapper>
          }
        />
        <Route
          path={PLANT_CHECKLIST}
          key="plant-checklist"
          element={
            <Wrapper context={context} permissions={permissions}>
              <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                <PlantChecklistPage />
              </FeatureTranslationsLoader>
            </Wrapper>
          }
        />
        <Route
          path={PLANT_CHECKLIST_UNSUPPORTED_KIND}
          key="plant-checklist-unsupported-kind"
          element={
            <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
              <UnsupportedChecklistKindErrorPage />
            </FeatureTranslationsLoader>
          }
        />
      </>
    )

  return null
}

const renderSystemsEngineerDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasSystemsEngineerDashboardPermission(permissions))
    return (
      <>
        <Route
          key="systems-engineer-dashboard"
          path={SYSTEMS_ENGINEER_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermission
                path={PLANT_SYSTEMS_ENGINEER_DASHBOARD}
                permission="VIEW_SYSTEM_ENGINEER_ENTRY_POINT"
              />
            </Wrapper>
          }
        />
        <Route
          key="plant-systems-engineer-dashboard"
          path={PLANT_SYSTEMS_ENGINEER_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionGuardedRoute
                permission="VIEW_SYSTEM_ENGINEER_ENTRY_POINT"
                plantSelectPath={SYSTEMS_ENGINEER_DASHBOARD}
              >
                <FeatureTranslationsLoader
                  translations={systemsEngineerDashboardFeatureTranslations}
                >
                  <ConfigProviderWrapper>
                    <SystemsEngineerDashboard permission="VIEW_SYSTEM_ENGINEER_ENTRY_POINT" />
                  </ConfigProviderWrapper>
                </FeatureTranslationsLoader>
              </PlantPermissionGuardedRoute>
            </Wrapper>
          }
        />
      </>
    )

  return null
}

const renderServiceLevelAgreementTrackingDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (hasServiceLevelAgreementTrackingPermission(permissions))
    return (
      <>
        <Route
          key="sla-tracking-dashboard"
          path={SERVICE_LEVEL_AGREEMENT_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermission
                path={PLANT_SERVICE_LEVEL_AGREEMENT_DASHBOARD}
                permission="VIEW_SLA_KPIS"
              />
            </Wrapper>
          }
        />
        <Route
          key="plant-sla-tracking-dashboard"
          path={PLANT_SERVICE_LEVEL_AGREEMENT_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionGuardedRoute
                permission="VIEW_SLA_KPIS"
                plantSelectPath={SERVICE_LEVEL_AGREEMENT_DASHBOARD}
              >
                <FeatureTranslationsLoader translations={slaTrackingFeatureTranslations}>
                  <ConfigProviderWrapper>
                    <ServiceLevelAgreementTrackingDashboard permission="VIEW_SLA_KPIS" />
                  </ConfigProviderWrapper>
                </FeatureTranslationsLoader>
              </PlantPermissionGuardedRoute>
            </Wrapper>
          }
        />
      </>
    )

  return null
}

const renderReliabilityEngineerDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (!hasViewReliabilityEngineerDashboardPermission(permissions)) return null

  return getLocationBasedNavigationRoutes({
    key: 'reliability-engineer-dashboard-routes',
    context,
    permissions,
    basePath: RELIABILITY_ENGINEER_DASHBOARD,
    permission: 'VIEW_RELIABILITY_ENGINEER_DASHBOARD',
    areaRouteElement: (
      <FeatureTranslationsLoader translations={reliabilityEngineerDashboardFeatureTranslations}>
        <AreaReliabilityEngineerDashboard />
      </FeatureTranslationsLoader>
    ),
    plantRouteElement: (
      <FeatureTranslationsLoader translations={reliabilityEngineerDashboardFeatureTranslations}>
        <ConfigProviderWrapper>
          <ReliabilityEngineerDashboard />
        </ConfigProviderWrapper>
      </FeatureTranslationsLoader>
    )
  })
}

const renderOptimizationSpecialistDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  if (!hasViewOptimizationSpecialistDashboardPermission(permissions)) return null

  return getLocationBasedNavigationRoutes({
    key: 'optimization-specialist-dashboard-routes',
    context,
    permissions,
    basePath: OPTIMIZATION_SPECIALIST_DASHBOARD,
    permission: 'VIEW_OPTIMIZATION_SPECIALIST_DASHBOARD',
    areaRouteElement: (
      <FeatureTranslationsLoader translations={optimizationSpecialistDashboardFeatureTranslations}>
        <AreaOptimizationSpecialistDashboard />
      </FeatureTranslationsLoader>
    ),
    plantRouteElement: (
      <FeatureTranslationsLoader translations={optimizationSpecialistDashboardFeatureTranslations}>
        <ConfigProviderWrapper>
          <PlantShiftsGuardedRoute goToLink={PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD}>
            <OptimizationSpecialistDashboard />
          </PlantShiftsGuardedRoute>
        </ConfigProviderWrapper>
      </FeatureTranslationsLoader>
    )
  })
}

const renderJobsToBeDoneDashboardRoutes = ({
  context,
  permissions
}: {
  context: Context
  permissions: Permission[]
}) => {
  const hasJobsToBeDonePermissions = hasJobsToBeDonePermission(permissions)

  const jobsToBeDonePermissions = [
    'VIEW_MAJOR_OUTAGE_LEAD_JTBD',
    'VIEW_OPTIMIZATION_SPECIALIST_JTBD',
    'VIEW_PROCESS_ENGINEER_JTBD',
    'VIEW_QUALITY_ENGINEER_JTBD',
    'VIEW_REGIONAL_MANAGER_JTBD',
    'VIEW_RELIABILITY_ENGINEER_JTBD',
    'VIEW_SYSTEM_ENGINEER_JTBD'
  ] as PermissionType[]

  if (hasJobsToBeDonePermissions)
    return (
      <>
        <Route
          key="jobs-to-be-done-dashboard"
          path={JOBS_TO_BE_DONE_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantSelectByPermissions
                path={PLANT_JOBS_TO_BE_DONE_DASHBOARD}
                permissions={jobsToBeDonePermissions}
              />
            </Wrapper>
          }
        />
        <Route
          key="plant-jobs-to-be-done-dashboard"
          path={PLANT_JOBS_TO_BE_DONE_DASHBOARD}
          element={
            <Wrapper context={context} permissions={permissions}>
              <PlantPermissionsGuardedRoute
                permissions={jobsToBeDonePermissions}
                plantSelectPath={JOBS_TO_BE_DONE_DASHBOARD}
              >
                <FeatureTranslationsLoader translations={jobsToBeDoneFeatureTranslations}>
                  <ConfigProviderWrapper>
                    <JobsToBeDoneDashboard permissions={jobsToBeDonePermissions} />
                  </ConfigProviderWrapper>
                </FeatureTranslationsLoader>
              </PlantPermissionsGuardedRoute>
            </Wrapper>
          }
        />
      </>
    )

  return null
}

const getLocationBasedNavigationRoutes = ({
  key,
  context,
  permissions,
  permission,
  basePath,
  areaRouteElement,
  plantRouteElement
}: {
  key: string
  context: Context
  permissions: Permission[]
  basePath: string
  permission: PermissionType
  areaRouteElement: React.ReactNode
  plantRouteElement: React.ReactNode
}) => [
  <Route
    key={key}
    path={basePath}
    element={
      <Wrapper context={context} permissions={permissions}>
        <Outlet />
      </Wrapper>
    }
  >
    <Route index element={<HierarchyLocationAccessRedirect permission={permission} />} />
    <Route path={HierarchyRouteSegment.AREA}>
      <Route
        index
        element={
          <AreaSelectByPermission
            path={`${HierarchyRouteSegment.AREA}/:areaId`}
            permission={permission}
          />
        }
      />
      <Route index path=":areaId" element={areaRouteElement} />
      <Route path=":areaId">
        <Route path={HierarchyRouteSegment.PLANT}>
          <Route
            index
            element={
              <AreaPlantSelectByPermission
                path={`${HierarchyRouteSegment.PLANT}/:plantId`}
                permission={permission}
                areaSelectPath={'../..'}
              />
            }
          />
          <Route
            path=":plantId"
            element={
              <PlantPermissionGuardedRoute permission={permission} plantSelectPath={'..'}>
                {plantRouteElement}
              </PlantPermissionGuardedRoute>
            }
          />
        </Route>
      </Route>
    </Route>
    <Route path={HierarchyRouteSegment.PLANT}>
      <Route
        index
        element={
          <PlantSelectByPermission
            path={`${HierarchyRouteSegment.PLANT}/:plantId`}
            permission={permission}
          />
        }
      />
      <Route
        path=":plantId"
        element={<HierarchyPlantAccessRedirect permission={permission} basePath={basePath} />}
      />
    </Route>
  </Route>
]
